import helper from '@/helper';
import * as routes from '@/routerNames';
import { Company } from '@/models/Company';
import apiService from '@/services/ApiService';
import navigationService from '@/services/NavigationService';
import { Mutations } from '@/store/mutations';
import { ActionContext } from 'vuex';
import { State } from '@/store/state';
import { User } from '@/models/User';
import { commitSetCompanyUsers } from '@/store/commits/companyCommits';
import { clearAppContext } from '@/helpers/appContext';

export const companyActions = {
  async switchCompany(
    { commit }: ActionContext<State, {}>,
    payload: string | { companyId: string; redirect?: string }
  ) {
    const companyId = typeof payload === 'string' ? payload : payload.companyId;
    const redirect = typeof payload === 'string' ? undefined : payload.redirect;

    const res = await apiService.company.switch(companyId).catch((err) => {
      apiService.handleResponseError(err);
      return null;
    });

    // SNAP-2311: the backend refuses (404) a switch into a company the user does not belong
    // to. Bail out before touching the store: the error has already been surfaced, and
    // continuing would clear the currently selected company and then navigate with no session.
    if (!res?.data?.company) {
      return;
    }

    // A company switch is a new journey. The token login below keeps a stored context on a
    // token visit, so a stale Guides context (e.g. left by the upsell + "back to Snaplore")
    // would upsell or force-redirect the NEW company.
    clearAppContext();

    const url = document.location.href;
    const draftId = helper.extractDraftId(url);
    const company = res.data.company ? Company.parse(res.data.company) : null;

    commit(Mutations.SET_COMPANY, company);
    commit('setArticleEditorEditDraftExtension', draftId);

    // SNAP-2309: the switch set the session cookie. session=1 makes the login page finish the switch.
    await navigationService.navigate({
      name: routes.LOGIN,
      query: { session: '1', ...(redirect ? { redirect, targetCompanyId: companyId } : {}) },
    });
  },
  async loadCompanyByHostname({ commit, state }: ActionContext<State, {}>) {
    const url = document.location.href;
    const res = await apiService.getDomainCompany(helper.extractHostname(url)).catch((err) => {
      apiService.handleResponseError(err);
      return null;
    });

    const company = res?.data?.company ? Company.parse(res.data.company) : null;

    // This races the router's /me. /domain is a public, smaller record (no `productAccess`, no subscription
    // fields), so it must not replace the same company once /me has loaded it (SNAP-2241).
    if (company && state.company?.id !== company.id) {
      commit(Mutations.SET_COMPANY, company);
    }
  },
  async loadCompanyUsers() {
    const response = await apiService.company.getUsers();

    if (response?.data?.users) {
      const users = response.data.users.map((user) => User.parse(user));
      commitSetCompanyUsers(users);
    }
  },
};
